import { formatTimestamp, formatTimeOnly, startOfDay } from "../lib/time";
import { useState, useMemo, useEffect, useRef } from 'react';
import axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import {
  Activity, PhoneCall, PhoneIncoming, BarChart3, RefreshCw,
  ChevronDown, ChevronUp, User, AlertTriangle, Search, Filter, Database, PlayCircle,
  Pause, Loader2, Settings2
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { DateRangePicker } from '../components/ui/date-range-picker';


const apiUrl = process.env.REACT_APP_BACKEND_URL;
const NUM_DAYS_OPTIONS = [1, 3, 7, 14, 30];
const MAX_RANGE_DAYS = 30;

/** Unique agents from current-stats schedule rows (`agent_id` + `agent_name`). */
function agentsFromSchedules(schedules) {
  const byId = new Map();
  for (const row of schedules || []) {
    const id = String(row?.agent_id || '').trim();
    if (!id) continue;
    const name = String(row?.agent_name || '').trim() || id;
    const prev = byId.get(id);
    if (!prev || (prev.agent_name === prev.agent_id && name !== id)) {
      byId.set(id, { agent_id: id, agent_name: name });
    }
  }
  return Array.from(byId.values()).sort((a, b) =>
    a.agent_name.localeCompare(b.agent_name, undefined, { sensitivity: 'base' })
  );
}

function mergeAgentOptions(prev, next) {
  const byId = new Map((prev || []).map((a) => [a.agent_id, a]));
  for (const a of next || []) byId.set(a.agent_id, a);
  return Array.from(byId.values()).sort((a, b) =>
    a.agent_name.localeCompare(b.agent_name, undefined, { sensitivity: 'base' })
  );
}

/** Free-text agent_id + suggestions from the last current-stats response. */
function AgentIdFilter({ value, onChange, agents }) {
  const [draft, setDraft] = useState(value || '');
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    setDraft(value || '');
  }, [value]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (!rootRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const q = draft.trim().toLowerCase();
  const suggestions = (agents || []).filter((a) => {
    if (!q) return true;
    return (
      a.agent_id.toLowerCase().includes(q)
      || a.agent_name.toLowerCase().includes(q)
    );
  });

  const apply = (next) => {
    const id = String(next || '').trim();
    setDraft(id);
    onChange(id);
    setOpen(false);
  };

  return (
    <div ref={rootRef} className="relative min-w-[14rem]">
      <div className="flex h-8 items-center rounded-lg border border-gray-200 bg-white focus-within:border-cyan-400 focus-within:ring-1 focus-within:ring-cyan-200">
        <input
          type="text"
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              apply(draft);
            } else if (e.key === 'Escape') {
              setDraft(value || '');
              setOpen(false);
            }
          }}
          onBlur={() => {
            // Apply typed id when leaving the field (mousedown on menu is handled first).
            window.setTimeout(() => {
              if (!rootRef.current?.contains(document.activeElement)) {
                const next = draft.trim();
                if (next !== (value || '')) onChange(next);
              }
            }, 0);
          }}
          placeholder="agent_id"
          className="h-full min-w-0 flex-1 rounded-lg bg-transparent px-2.5 text-xs text-gray-800 placeholder:text-gray-400 focus:outline-none"
          aria-autocomplete="list"
          aria-expanded={open}
        />
        {(draft || value) && (
          <button
            type="button"
            onClick={() => apply('')}
            className="px-1.5 text-xs text-gray-400 hover:text-gray-700"
            title="Clear agent filter"
          >
            ×
          </button>
        )}
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="px-2 text-gray-400 hover:text-gray-700"
          aria-label="Show agents from last response"
        >
          {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        </button>
      </div>
      {open && (
        <div className="absolute right-0 z-50 mt-1 max-h-64 w-72 overflow-y-auto rounded-xl border border-gray-200 bg-white py-1 shadow-lg">
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => apply('')}
            className={`flex w-full items-center px-3 py-2 text-left text-xs hover:bg-gray-50 ${!value ? 'bg-cyan-50 font-semibold text-cyan-700' : 'text-gray-700'}`}
          >
            All agents
          </button>
          {suggestions.length === 0 ? (
            <p className="px-3 py-2 text-xs text-gray-400">
              {agents?.length
                ? 'No match — press Enter to use this agent_id'
                : 'No agents in last response — type an agent_id and press Enter'}
            </p>
          ) : (
            suggestions.map((a) => (
              <button
                key={a.agent_id}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => apply(a.agent_id)}
                className={`flex w-full flex-col gap-0.5 px-3 py-2 text-left hover:bg-gray-50 ${
                  value === a.agent_id ? 'bg-cyan-50' : ''
                }`}
              >
                <span className="text-xs font-medium text-gray-900 truncate">{a.agent_name}</span>
                <span className="font-mono text-[10px] text-gray-500 truncate">{a.agent_id}</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function toYmd(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function inclusiveDayCount(start, end) {
  const s = startOfDay(start).getTime();
  const e = startOfDay(end).getTime();
  return Math.floor((e - s) / 864e5) + 1;
}

/** Clamp to an inclusive window of at most MAX_RANGE_DAYS (API limit). */
function clampDateRange(start, end) {
  const s = startOfDay(start);
  let e = startOfDay(end);
  if (e < s) e = s;
  if (inclusiveDayCount(s, e) > MAX_RANGE_DAYS) {
    e = startOfDay(new Date(s.getTime() + (MAX_RANGE_DAYS - 1) * 864e5));
  }
  return { start: s, end: e };
}

function lookbackRange(numDays) {
  const end = startOfDay(new Date());
  const start = startOfDay(new Date(Date.now() - (Math.max(1, numDays) - 1) * 864e5));
  return { start, end, preset: null };
}

// Same definition as the overall Connect Rate card: completed / (completed + failed).
function connectRatePercent(completed, failed) {
  const finished = completed + failed;
  if (finished <= 0) return null;
  return Math.round((completed / finished) * 100);
}

function readCount(source, key) {
  if (!source || source[key] == null || source[key] === '') return null;
  const value = parseInt(source[key], 10);
  return Number.isFinite(value) ? value : null;
}

function uniquePhoneStats(primary, fallback) {
  const completed = readCount(primary, 'unique_customer_phones_completed')
    ?? readCount(fallback, 'unique_customer_phones_completed')
    ?? 0;
  const notCompleted = readCount(primary, 'unique_customer_phones_not_completed')
    ?? readCount(fallback, 'unique_customer_phones_not_completed')
    ?? 0;
  const total = completed + notCompleted;
  return {
    completed,
    notCompleted,
    total,
    successRate: total > 0 ? Math.round((completed / total) * 100) : null,
  };
}

function UniquePhoneSummary({ phones }) {
  if (!phones) return null;
  const items = [
    { label: 'Unique contacts', value: phones.total.toLocaleString(), tone: 'text-gray-900' },
    { label: 'Completed', value: phones.completed.toLocaleString(), tone: 'text-emerald-600' },
    { label: 'Not completed', value: phones.notCompleted.toLocaleString(), tone: 'text-rose-600' },
    { label: 'Success', value: phones.successRate !== null ? `${phones.successRate}%` : '—', tone: 'text-cyan-600' },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      {items.map((item) => (
        <div key={item.label} className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
          <div className="text-xs uppercase tracking-wide text-gray-500">{item.label}</div>
          <div className={`mt-1 font-mono text-2xl font-semibold tabular-nums ${item.tone}`}>{item.value}</div>
        </div>
      ))}
    </div>
  );
}

/** Rollup across all telephony providers: `{ total, completed, failed, … }`. */
function normaliseTelephonyTotal(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const completed = parseInt(raw.completed || 0, 10) || 0;
  const failed = parseInt(raw.failed || 0, 10) || 0;
  const total = parseInt(raw.total || 0, 10) || completed + failed;
  if (total <= 0 && completed <= 0 && failed <= 0) return null;
  return {
    provider: 'All providers',
    completed,
    failed,
    total,
    connectRate: connectRatePercent(completed, failed),
    bySip: [],
  };
}

function normaliseTelephonyProviders(raw) {
  if (!raw || typeof raw !== 'object') return [];
  return Object.entries(raw)
    .map(([provider, stats]) => {
      const completed = parseInt(stats?.completed || 0, 10) || 0;
      const failed = parseInt(stats?.failed || 0, 10) || 0;
      const reportedTotal = parseInt(stats?.total || 0, 10) || 0;
      const bySip = (Array.isArray(stats?.by_sip) ? stats.by_sip : [])
        .map((row) => {
          const success = row?.sip_status_code == null || row?.sip_status_code === '';
          const reason = String(row?.reason || '').trim();
          return {
            code: success ? 'Success' : String(row.sip_status_code),
            success,
            reason,
            count: parseInt(row?.count || 0, 10) || 0,
            messages: Object.entries(row?.sip_status || {})
              .map(([message, detail]) => {
                // Backend may send a plain count or { count, sip_failed_reason: { label: n } }.
                const isNested = detail && typeof detail === 'object' && !Array.isArray(detail);
                const count = parseInt(isNested ? detail.count : detail, 10) || 0;
                const failedReasons = isNested
                  ? Object.entries(detail.sip_failed_reason || {})
                      .map(([label, reasonCount]) => ({
                        label: String(label),
                        count: parseInt(reasonCount || 0, 10) || 0,
                      }))
                      .filter((r) => r.count > 0)
                      .sort((a, b) => b.count - a.count)
                  : [];
                return {
                  message: String(message),
                  count,
                  failedReasons,
                };
              })
              .sort((a, b) => b.count - a.count),
          };
        })
        .sort((a, b) => {
          if (a.success !== b.success) return a.success ? -1 : 1;
          return b.count - a.count;
        });
      return {
        provider,
        completed,
        failed,
        total: reportedTotal || completed + failed,
        connectRate: connectRatePercent(completed, failed),
        bySip,
      };
    })
    .filter((row) => row.total > 0 || row.bySip.length > 0)
    .sort((a, b) => {
      const aUnknown = a.provider.toLowerCase() === 'unknown';
      const bUnknown = b.provider.toLowerCase() === 'unknown';
      if (aUnknown !== bUnknown) return aUnknown ? 1 : -1;
      return b.total - a.total;
    });
}

function formatShare(count, total) {
  if (!total) return '—';
  const pct = (count / total) * 100;
  if (pct > 0 && pct < 0.1) return '<0.1%';
  return `${Math.round(pct * 10) / 10}%`;
}

function ShareFigures({ count, total, emphasize = false }) {
  return (
    <div className="flex shrink-0 items-baseline gap-3">
      <span className={`w-16 text-right font-mono tabular-nums font-semibold text-gray-900 ${emphasize ? 'text-base' : 'text-sm'}`}>
        {count.toLocaleString()}
      </span>
      <span className={`w-14 text-right font-mono tabular-nums ${emphasize ? 'text-sm text-gray-700' : 'text-sm text-gray-500'}`}>
        {formatShare(count, total)}
      </span>
    </div>
  );
}

function SipStatusMessageRow({ message, total }) {
  const [reasonsOpen, setReasonsOpen] = useState(false);
  const canExpandReasons = message.failedReasons?.length > 0;

  const header = (
    <div className="flex flex-col gap-1 px-4 py-2 pl-10 sm:flex-row sm:items-start sm:gap-3">
      <div className="min-w-0 flex-1 flex items-start gap-2">
        {canExpandReasons ? (
          reasonsOpen ? (
            <ChevronUp className="w-3.5 h-3.5 text-gray-400 shrink-0 mt-0.5" />
          ) : (
            <ChevronDown className="w-3.5 h-3.5 text-gray-400 shrink-0 mt-0.5" />
          )
        ) : (
          <span className="w-3.5 shrink-0" />
        )}
        <span className="min-w-0 flex-1 text-sm text-gray-600 break-words" title={message.message}>
          {message.message}
        </span>
      </div>
      <div className="self-end sm:self-auto">
        <ShareFigures count={message.count} total={total} />
      </div>
    </div>
  );

  return (
    <div>
      {canExpandReasons ? (
        <button
          type="button"
          onClick={() => setReasonsOpen((value) => !value)}
          aria-expanded={reasonsOpen}
          className="w-full text-left hover:bg-gray-50/80"
        >
          {header}
        </button>
      ) : (
        header
      )}
      {reasonsOpen && canExpandReasons && (
        <div className="border-t border-gray-50">
          {message.failedReasons.map((failedReason) => (
            <div
              key={`${message.message}-${failedReason.label}`}
              className="flex flex-col gap-1 px-4 py-1.5 pl-16 sm:flex-row sm:items-center sm:gap-3"
            >
              <span
                className="min-w-0 flex-1 text-sm text-gray-500 break-words capitalize"
                title={failedReason.label}
              >
                {failedReason.label}
              </span>
              <div className="self-end sm:self-auto">
                <ShareFigures count={failedReason.count} total={total} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SipOutcomeRow({ sip, total }) {
  const [messagesOpen, setMessagesOpen] = useState(false);
  const share = total ? Math.min((sip.count / total) * 100, 100) : 0;
  const canExpandMessages = sip.messages.length > 0;

  const header = (
    <div className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:gap-3">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
          {canExpandMessages ? (
            messagesOpen ? (
              <ChevronUp className="w-4 h-4 text-gray-400 shrink-0" />
            ) : (
              <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />
            )
          ) : (
            <span className="w-4 shrink-0" />
          )}
          <span className={`text-sm font-semibold ${sip.success ? 'text-emerald-700' : 'text-gray-900'}`}>
            {sip.success ? 'Success' : `SIP ${sip.code}`}
          </span>
          {sip.reason && (
            <span className="text-base font-medium text-gray-700">({sip.reason})</span>
          )}
        </div>
        <div className="mt-1.5 ml-6 h-1.5 rounded-full bg-gray-200 overflow-hidden">
          <div
            className={`h-full rounded-full ${sip.success ? 'bg-emerald-500' : 'bg-rose-400'}`}
            style={{ width: `${Math.min(share, 100)}%` }}
          />
        </div>
      </div>
      <div className="self-end sm:self-auto">
        <ShareFigures count={sip.count} total={total} emphasize />
      </div>
    </div>
  );

  return (
    <div className={sip.success ? 'bg-emerald-50/70' : 'bg-white'}>
      {canExpandMessages ? (
        <button
          type="button"
          onClick={() => setMessagesOpen((value) => !value)}
          aria-expanded={messagesOpen}
          className="w-full text-left hover:bg-gray-50/80"
        >
          {header}
        </button>
      ) : (
        header
      )}
      {messagesOpen && canExpandMessages && (
        <div className="border-t border-gray-100">
          {sip.messages.map((message) => (
            <SipStatusMessageRow key={message.message} message={message} total={total} />
          ))}
        </div>
      )}
    </div>
  );
}

function TelephonyProviderCard({ row }) {
  const [open, setOpen] = useState(false);
  const canExpand = row.bySip.length > 0;

  return (
    <div className="px-5 py-4">
      {canExpand ? (
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          aria-expanded={open}
          className="w-full text-left rounded-xl hover:bg-gray-50 -mx-2 px-2 py-2"
        >
          <TelephonyProviderSummary row={row} open={open} />
        </button>
      ) : (
        <div className="px-2 py-2">
          <TelephonyProviderSummary row={row} />
        </div>
      )}
      {open && canExpand && (
        <div className="mt-3 overflow-hidden rounded-xl border border-gray-200">
          <div className="flex items-center gap-3 px-4 py-2 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
            <span className="min-w-0 flex-1">Outcome</span>
            <span className="w-16 text-right">Count</span>
            <span className="w-14 text-right">Share</span>
          </div>
          {row.bySip.map((sip) => (
            <SipOutcomeRow key={`${row.provider}-${sip.code}`} sip={sip} total={row.total} />
          ))}
        </div>
      )}
    </div>
  );
}

function TelephonyProviderSummary({ row, open = null }) {
  return (
    <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
      <div className="flex items-center gap-2 min-w-0">
        {open === null ? (
          <span className="w-5 shrink-0" />
        ) : open ? (
          <ChevronUp className="w-5 h-5 text-gray-500 shrink-0" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-500 shrink-0" />
        )}
        <div className="text-lg font-semibold text-gray-900 truncate">{row.provider}</div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {[
          { label: 'Dialled', value: row.total.toLocaleString(), tone: 'text-gray-900' },
          { label: 'Completed', value: row.completed.toLocaleString(), tone: 'text-emerald-600' },
          { label: 'Failed', value: row.failed.toLocaleString(), tone: 'text-rose-600' },
          { label: 'Connect', value: row.connectRate !== null ? `${row.connectRate}%` : '—', tone: 'text-cyan-600' },
        ].map((stat) => (
          <div key={stat.label} className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 min-w-[7rem]">
            <div className="text-xs uppercase tracking-wide text-gray-500">{stat.label}</div>
            <div className={`mt-0.5 font-mono text-lg font-semibold tabular-nums ${stat.tone}`}>{stat.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TelephonyProvidersSection({ providers, total = null }) {
  const [open, setOpen] = useState(false);
  if (!providers?.length && !total) return null;
  const dialled = total?.total ?? providers.reduce((sum, row) => sum + row.total, 0);
  const connect = total?.connectRate ?? null;
  const canExpand = providers.length > 0;

  return (
    <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden shadow-sm">
      {canExpand ? (
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          className="w-full px-5 py-4 flex items-center justify-between gap-4 text-left hover:bg-gray-50"
        >
          <div className="min-w-0 flex-1">
            <div className="text-base font-semibold text-gray-900">Telephony providers</div>
            <div className="text-sm text-gray-500 mt-1">
              {providers.length} provider{providers.length === 1 ? '' : 's'} · {dialled.toLocaleString()} dialled
              {connect !== null ? ` · ${connect}% connect` : ''}
            </div>
          </div>
          {open ? <ChevronUp className="w-5 h-5 text-gray-500 shrink-0" /> : <ChevronDown className="w-5 h-5 text-gray-500 shrink-0" />}
        </button>
      ) : (
        <div className="px-5 py-4">
          <div className="text-base font-semibold text-gray-900">Telephony providers</div>
          <div className="text-sm text-gray-500 mt-1">All providers rollup</div>
        </div>
      )}
      {/* All-providers rollup from dialed_calls_by_telephony_provider_total */}
      {(open || !canExpand) && total && (
        <div className="border-t border-gray-200 px-5 py-4 bg-gray-50/60">
          <TelephonyProviderSummary row={total} />
        </div>
      )}
      {open && canExpand && (
        <div className="border-t border-gray-200 divide-y divide-gray-200">
          {providers.map((row) => (
            <TelephonyProviderCard key={row.provider} row={row} />
          ))}
        </div>
      )}
    </div>
  );
}

function useCurrentStats({ numDays, startDate, endDate, useDateRange, agentId }, token) {
  return useQuery({
    queryKey: [
      'admin',
      'current-stats',
      useDateRange ? 'range' : 'days',
      useDateRange ? startDate : numDays,
      useDateRange ? endDate : null,
      agentId || null,
    ],
    queryFn: async () => {
      const params = {};
      if (useDateRange && startDate && endDate) {
        params.start_date = startDate;
        params.end_date = endDate;
      } else {
        params.num_days = numDays;
      }
      if (agentId) params.agent_id = agentId;
      const res = await axios.get(`${apiUrl}/api/v1/admin/current-stats`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      return res.data;
    },
    enabled: Boolean(token),
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  });
}

function useDerivedViewModel(data, searchQ, filterStatus, { numDays, startDate, endDate, useDateRange }) {
  return useMemo(() => {
    if (!data) return null;

    const rawStats = data.stats || {};
    let rawSchedules = data.schedules || [];

    // Filter schedules to the selected window. Exception: always keep batches that
    // still have pending work (callbacks, PCA, or primary calls).
    let windowStart;
    let windowEnd = null;
    if (useDateRange && startDate && endDate) {
      windowStart = new Date(`${startDate}T00:00:00.000Z`);
      windowEnd = new Date(`${endDate}T23:59:59.999Z`);
    } else {
      windowStart = new Date();
      windowStart.setUTCHours(0, 0, 0, 0);
      windowStart.setUTCDate(windowStart.getUTCDate() - Math.max(0, numDays - 1));
    }

    rawSchedules = rawSchedules.filter(s => {
      const hasWork =
        parseInt(s.stats?.pending_callbacks_count || 0) > 0 ||
        parseInt(s.stats?.pending_pca_count        || 0) > 0 ||
        parseInt(s.stats?.pending_primary_call_count || 0) > 0;
      if (hasWork) return true;      // always show if still running
      if (!s.created_at) return true;
      const created = new Date(s.created_at);
      if (created < windowStart) return false;
      if (windowEnd && created > windowEnd) return false;
      return true;
    });

    const activeCalls = parseInt(rawStats.running_calls_count || 0);
    const totalPending = (parseInt(rawStats.pending_primary_call_count || 0) + parseInt(rawStats.pending_callbacks_count || 0) + parseInt(rawStats.pending_pca_count || 0));

    const pauseSettings = data.admin_settings?.pause_settings || {};
    const isGloballyPaused = !!pauseSettings.pause_all_calls;

    let globalStatus = "idle";
    let statusMessage = "System idle";
    let statusDesc = "No active conversations or pending queues right now.";

    if (isGloballyPaused) {
      globalStatus = "paused";
      statusMessage = "Global pause active";
      statusDesc = `All dialing is paused. ${totalPending.toLocaleString()} tasks are queued and waiting to resume.`;
    } else if (activeCalls > 0) {
      globalStatus = "live";
      statusMessage = "Live dialing active";
      statusDesc = `${activeCalls} AI conversations in progress • ${totalPending.toLocaleString()} tasks queued`;
    } else if (totalPending > 0) {
      globalStatus = "queued";
      statusMessage = "Queues active, no live calls";
      statusDesc = "System is processing queues or paused.";
    }

    let globalContactsTotal = 0;
    let globalContactsAccepted = 0;
    let globalContactsRejected = 0;

    const campaigns = rawSchedules.map(s => {
      const pPrimary = parseInt(s.stats?.pending_primary_call_count || 0);
      const pCallbacks = parseInt(s.stats?.pending_callbacks_count || 0);
      const pPca = parseInt(s.stats?.pending_pca_count || 0);
      const cTotalPending = pPrimary + pCallbacks + pPca;
      const cActiveCalls = 0; // Backend no longer provides per-schedule live running calls
      const cAccepted = parseInt(s.accepted_contacts || 0);
      const cRejected = parseInt(s.rejected_contacts || 0);
      const cTotal = parseInt(s.total_contacts || 0);

      // Capture ALL status keys dynamically (not just failed/completed)
      const rawDialedByStatus = s.stats?.dialed_calls_by_status || {};
      const cDialedByStatus = Object.fromEntries(
        Object.entries(rawDialedByStatus).map(([k, v]) => [k, parseInt(v || 0)])
      );
      const cDialedCompleted = cDialedByStatus.completed || 0;
      const cDialedFailed = cDialedByStatus.failed || 0;
      // Real dialled = sum of all status counts (more accurate than total_contacts - pending_primary)
      const cTotalCalls = Object.values(cDialedByStatus).reduce((a, b) => a + b, 0);

      const cCompletedCallbacks = parseInt(s.stats?.completed_callbacks_count || 0);
      const cCompletedOver15s = parseInt(s.stats?.completed_over_15s_count || 0);
      const cConnectRate = connectRatePercent(cDialedCompleted, cDialedFailed);
      const cUniquePhones = uniquePhoneStats(s.stats, s);

      // Only count contacts from schedules created within the date window
      const createdAt = s.created_at ? new Date(s.created_at) : null;
      const createdInWindow = !createdAt
        || (createdAt >= windowStart && (!windowEnd || createdAt <= windowEnd));
      if (createdInWindow) {
        globalContactsTotal += cTotal;
        globalContactsAccepted += cAccepted;
        globalContactsRejected += cRejected;
      }

      const urgencyScore = (pPrimary * 3) + (pCallbacks * 2) + (pPca * 1);

      let cStatus = "clean";
      if (cTotalPending > 0) cStatus = "queued";

      const completion = cTotal > 0 ? Math.min(cTotalCalls / cTotal, 1) : 0;

      return {
        id: s.schedule_id,
        agentName: s.agent_name,
        versionName: s.version_name,
        clientName: s.user?.name || s.client_id,
        createdAt: s.created_at,
        status: cStatus,
        urgencyScore,
        progress: {
          percent: completion,
          dialled: cTotalCalls,
          acceptedContacts: cAccepted
        },
        live: { activeCalls: cActiveCalls },
        queues: { primary: pPrimary, callbacks: pCallbacks, pca: pPca, total: cTotalPending },
        contacts: { total: cTotal, accepted: cAccepted, rejected: cRejected },
        dialedByStatus: cDialedByStatus, // full dict with all status keys
        completedCallbacks: cCompletedCallbacks,
        completedOver15s: cCompletedOver15s,
        connectRate: cConnectRate,
        uniquePhones: cUniquePhones,
        telephonyProviders: normaliseTelephonyProviders(s.stats?.dialed_calls_by_telephony_provider),
        telephonyTotal: normaliseTelephonyTotal(s.stats?.dialed_calls_by_telephony_provider_total),
        pauseSettings: s.pause_settings || {}
      };
    });

    let filtered = campaigns.filter(c => {
      if (searchQ) {
        const q = searchQ.toLowerCase();
        if (!c.id?.toLowerCase().includes(q) &&
            !c.agentName?.toLowerCase().includes(q) &&
            !c.clientName?.toLowerCase().includes(q) &&
            !c.versionName?.toLowerCase().includes(q)) {
          return false;
        }
      }
      if (filterStatus !== 'all') {
        if (filterStatus === 'needs_attention') return c.status === 'queued';
        return c.status === filterStatus;
      }
      return true;
    });

    filtered.sort((a, b) => {
      if (b.urgencyScore !== a.urgencyScore) return b.urgencyScore - a.urgencyScore;
      return b.live.activeCalls - a.live.activeCalls;
    });

    // Capture ALL global dialed_calls_by_status keys dynamically
    const rawGDialedByStatus = rawStats.dialed_calls_by_status || {};
    const gDialedByStatus = Object.fromEntries(
      Object.entries(rawGDialedByStatus).map(([k, v]) => [k, parseInt(v || 0)])
    );
    const gDialedCompleted = gDialedByStatus.completed || 0;
    const gDialedFailed = gDialedByStatus.failed || 0;
    const gCompletedCallbacks = parseInt(rawStats.completed_callbacks_count || 0);
    const gCompletedOver15s = parseInt(rawStats.completed_over_15s_count || 0);
    const gConnectRate = connectRatePercent(gDialedCompleted, gDialedFailed);
    const gUniquePhones = uniquePhoneStats(rawStats, data);

    return {
      status: { global: globalStatus, message: statusMessage, description: statusDesc },
      global: {
        live: { activeCalls },
        backlog: {
          total: totalPending,
          primary: parseInt(rawStats.pending_primary_call_count || 0),
          callbacks: parseInt(rawStats.pending_callbacks_count || 0),
          pca: parseInt(rawStats.pending_pca_count || 0)
        },
        throughput: {
          totalCalls: Object.values(gDialedByStatus).reduce((a, b) => a + b, 0),
          dialedByStatus: gDialedByStatus, // full dict with all status keys
          failed: gDialedFailed,
          completed: gDialedCompleted,
          completedCallbacks: gCompletedCallbacks,
          completedOver15s: gCompletedOver15s,
          connectRate: gConnectRate,
          telephonyProviders: normaliseTelephonyProviders(rawStats.dialed_calls_by_telephony_provider),
          telephonyTotal: normaliseTelephonyTotal(rawStats.dialed_calls_by_telephony_provider_total),
        },
        contacts: {
          total: globalContactsTotal,
          accepted: globalContactsAccepted,
          rejected: globalContactsRejected
        },
        uniquePhones: gUniquePhones,
        campaigns: { total: rawSchedules.length }
      },
      campaigns: filtered,
      adminSettings: data.admin_settings
    };
  }, [data, searchQ, filterStatus, numDays, startDate, endDate, useDateRange]);
}

function ConfirmDialog({ isOpen, title, message, onConfirm, onCancel, isLoading }) {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        <div className="p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-2">{title}</h3>
          <p className="text-sm text-gray-500 mb-6">{message}</p>
          <div className="flex justify-end gap-3">
            <button 
              onClick={onCancel} 
              disabled={isLoading}
              className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button 
              onClick={onConfirm}
              disabled={isLoading}
              className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-cyan-600 hover:bg-cyan-700 transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
              Confirm
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function PauseButton({ isPaused, onClick, disabled, label = "", pauseColorClass = "bg-rose-500 hover:bg-rose-600" }) {
  const btnText = label ? ` ${label}` : "";
  if (isPaused) {
    return (
      <button onClick={onClick} disabled={disabled} className="px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 shadow-sm bg-emerald-500 hover:bg-emerald-600 text-white disabled:opacity-50">
        <PlayCircle className="w-3.5 h-3.5" />
        Resume{btnText}
      </button>
    );
  }
  return (
    <button onClick={onClick} disabled={disabled} className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 shadow-sm text-white disabled:opacity-50 ${pauseColorClass}`}>
      <Pause className="w-3.5 h-3.5" />
      Pause{btnText}
    </button>
  );
}

function WaveformIndicator({ active }) {
  if (!active) return (
    <div className="flex items-center gap-1 h-12 shrink-0 opacity-20">
      <div className="w-2 h-2 bg-white/50 rounded-full"></div>
    </div>
  );
  return (
    <div className="flex items-end gap-1.5 h-12 shrink-0">
      <div className="w-2 h-6 bg-cyan-400 rounded-full animate-[pulse_1s_infinite]"></div>
      <div className="w-2 h-12 bg-cyan-400 rounded-full animate-[pulse_1.2s_infinite_0.1s]"></div>
      <div className="w-2 h-8 bg-cyan-400 rounded-full animate-[pulse_0.9s_infinite_0.2s]"></div>
      <div className="w-2 h-4 bg-cyan-400 rounded-full animate-[pulse_1.1s_infinite_0.3s]"></div>
    </div>
  );
}

function GlassCard({ children, className = "" }) {
  return (
    <div className={`rounded-2xl border border-gray-200 bg-white backdrop-blur-xl shadow-sm ${className}`}>
      {children}
    </div>
  );
}

function CampaignRow({ campaign, onToggleSchedulePause, updatingScheduleKey, globalPauseSettings = {} }) {
  const { user } = useAuth() || {};
  const [expanded, setExpanded] = useState(false);
  
  const statusColors = {
    live: "bg-emerald-500",
    queued: "bg-amber-500",
    clean: "bg-gray-300"
  };
  
  const statusBadges = {
    live: <span className="flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-[10px] font-bold uppercase tracking-wider"><div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Live</span>,
    queued: <span className="flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-500 text-[10px] font-bold uppercase tracking-wider">Queued</span>,
    clean: <span className="flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-gray-100 border border-gray-200 text-gray-500 text-[10px] font-bold uppercase tracking-wider">Clean</span>,
  };

  const createdAt = campaign.createdAt ? new Date(campaign.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '–';

  return (
    <GlassCard className="overflow-hidden hover:bg-gray-50 hover:bg-gray-100 hover:border-gray-300 transition-all duration-300">
      <div className="flex">
        {/* Status Rail */}
        <div className={`w-1 shrink-0 ${statusColors[campaign.status]}`} />
        
        <div className="flex-1">
          <button 
            className="w-full text-left px-5 py-4 flex flex-col md:flex-row md:items-center justify-between gap-4"
            onClick={() => setExpanded(!expanded)}
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center flex-wrap gap-x-3 gap-y-1 mb-1">
                <span className="font-semibold text-gray-900 truncate text-base">{campaign.agentName}</span>
                <span className="px-2 py-0.5 rounded-full bg-gray-100 border border-gray-200 text-[10px] font-mono text-gray-500">{campaign.versionName}</span>
                {statusBadges[campaign.status]}
              </div>
              <div className="flex items-center flex-wrap gap-x-3 gap-y-1 text-xs text-gray-500 font-mono">
                <span className="flex items-center gap-1"><User className="w-3 h-3"/> {campaign.clientName}</span>
                <span>•</span>
                <span>{createdAt}</span>
                <span>•</span>
                <span className="truncate">ID: {campaign.id.split('-')[0]}...</span>
                {(campaign.pauseSettings?.pause_all_calls || campaign.pauseSettings?.pause_primary_calls || campaign.pauseSettings?.pause_scheduled_callbacks) && (
                  <span className="flex items-center gap-1 text-rose-500 font-bold ml-2">
                    <Pause className="w-3 h-3" /> Paused
                  </span>
                )}
              </div>
            </div>

            <div className="flex items-center gap-8 shrink-0">
              <div className="hidden lg:flex items-center gap-6">
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Contacts</div>
                  <div className="font-mono text-gray-800 font-variant-numeric: tabular-nums">{campaign.contacts.total.toLocaleString()}</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Dialled</div>
                  <div className="font-mono text-cyan-300 font-variant-numeric: tabular-nums">{campaign.progress.dialled.toLocaleString()}</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Connect</div>
                  <div className="font-mono text-cyan-500 font-variant-numeric: tabular-nums">
                    {campaign.connectRate !== null ? `${campaign.connectRate}%` : '—'}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Live</div>
                  <div className={`font-mono font-variant-numeric: tabular-nums ${campaign.live.activeCalls > 0 ? 'text-emerald-400 font-bold' : 'text-gray-500'}`}>
                    {campaign.live.activeCalls}
                  </div>
                </div>
              </div>
              
              <div className="w-32 hidden md:block">
                <div className="flex justify-between text-[10px] text-gray-500 mb-1.5 font-mono">
                  <span>Progress</span>
                  <span>{Math.round(campaign.progress.percent * 100)}%</span>
                </div>
                <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-cyan-400/80 transition-all duration-500" style={{ width: `${campaign.progress.percent * 100}%` }} />
                </div>
              </div>

              {expanded ? <ChevronUp className="w-4 h-4 text-gray-500" /> : <ChevronDown className="w-4 h-4 text-gray-500" />}
            </div>
          </button>

          {expanded && (
            <div className="px-5 pb-5 pt-2 border-t border-gray-200 animate-in slide-in-from-top-2 fade-in duration-300">
              <div className="mb-6">
                <div className="flex justify-between text-xs font-mono text-gray-600 mb-2">
                  <span>Batch Completion</span>
                  <span>{campaign.progress.dialled.toLocaleString()} dialled / {campaign.contacts.accepted.toLocaleString()} accepted</span>
                </div>
                <div className="h-2.5 w-full bg-gray-100 rounded-full overflow-hidden shadow-inner">
                  <div className="h-full bg-gradient-to-r from-cyan-500 to-blue-500 transition-all duration-500" style={{ width: `${campaign.progress.percent * 100}%` }} />
                </div>
              </div>

              <div className="mb-4">
                <div className="text-sm font-semibold text-gray-900 mb-2">Unique contacts</div>
                <UniquePhoneSummary phones={campaign.uniquePhones} />
              </div>

              {/* Queues */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="bg-white border border-gray-200 rounded-xl p-4">
                  <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-2 flex items-center gap-1.5"><Activity className="w-3 h-3" /> Live Calls</div>
                  <div className={`text-2xl font-mono ${campaign.live.activeCalls > 0 ? 'text-emerald-400 font-bold animate-pulse' : 'text-gray-500'}`}>
                    {campaign.live.activeCalls}
                  </div>
                </div>
                <div className="bg-white border border-gray-200 rounded-xl p-4">
                  <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-2 flex items-center gap-1.5"><PhoneCall className="w-3 h-3" /> Pending Primary</div>
                  <div className="text-2xl font-mono text-gray-900">{campaign.queues.primary.toLocaleString()}</div>
                </div>
                <div className="bg-white border border-gray-200 rounded-xl p-4">
                  <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-2 flex items-center gap-1.5"><PhoneIncoming className="w-3 h-3" /> Pending Callbacks</div>
                  <div className="text-2xl font-mono text-amber-400">{campaign.queues.callbacks.toLocaleString()}</div>
                </div>
                <div className="bg-white border border-gray-200 rounded-xl p-4">
                  <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-2 flex items-center gap-1.5"><BarChart3 className="w-3 h-3" /> Pending PCA</div>
                  <div className="text-2xl font-mono text-violet-400">{campaign.queues.pca.toLocaleString()}</div>
                </div>
              </div>

              {/* Dialed by Status — dynamic, renders ALL status keys from backend */}
              <div className="mt-3">
                <div className="text-[10px] uppercase tracking-widest text-gray-400 mb-2 font-mono">Call Outcomes</div>
                <div className={`grid gap-3 ${Object.keys(campaign.dialedByStatus).length <= 2 ? 'grid-cols-2' : Object.keys(campaign.dialedByStatus).length === 3 ? 'grid-cols-3' : 'grid-cols-2 sm:grid-cols-4'}`}>
                  {Object.entries(campaign.dialedByStatus)
                    .sort(([a], [b]) => a === 'completed' ? -1 : b === 'completed' ? 1 : a === 'failed' ? -1 : b === 'failed' ? 1 : a.localeCompare(b))
                    .map(([status, count]) => {
                      const isCompleted = status === 'completed';
                      const isFailed = status === 'failed';
                      const style = isCompleted
                        ? { bg: 'bg-emerald-50 border-emerald-100', text: 'text-emerald-600', label: 'text-emerald-600' }
                        : isFailed
                        ? { bg: 'bg-rose-50 border-rose-100', text: 'text-rose-600', label: 'text-rose-600' }
                        : { bg: 'bg-gray-50 border-gray-200', text: 'text-gray-700', label: 'text-gray-500' };
                      return (
                        <div key={status} className={`border rounded-xl p-4 ${style.bg}`}>
                          <div className={`text-[10px] uppercase tracking-widest mb-2 ${style.label}`}>
                            {status.replace(/-/g, ' ')}
                          </div>
                          <div className={`text-2xl font-mono font-bold ${style.text}`}>{count.toLocaleString()}</div>
                        </div>
                      );
                    })
                  }
                </div>
              </div>
              {/* Same trio as the overall KPI row: callbacks, calls >15s, connect rate */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-3">
                <div className="bg-amber-50 border border-amber-100 rounded-xl p-4">
                  <div className="text-[10px] uppercase tracking-widest text-amber-600 mb-2">Completed Callbacks</div>
                  <div className="text-2xl font-mono font-bold text-amber-600">{campaign.completedCallbacks.toLocaleString()}</div>
                </div>
                <div className="bg-violet-50 border border-violet-100 rounded-xl p-4">
                  <div className="text-[10px] uppercase tracking-widest text-violet-600 mb-2">Calls &gt;15s</div>
                  <div className="text-2xl font-mono font-bold text-violet-600">{campaign.completedOver15s.toLocaleString()}</div>
                  <div className="text-[10px] font-mono text-violet-400 mt-1">
                    {(campaign.dialedByStatus.completed || 0) > 0
                      ? `${Math.round((campaign.completedOver15s / campaign.dialedByStatus.completed) * 100)}% of completed`
                      : 'of completed'}
                  </div>
                </div>
                <div className="bg-cyan-50 border border-cyan-100 rounded-xl p-4">
                  <div className="text-[10px] uppercase tracking-widest text-cyan-600 mb-2">Connect Rate</div>
                  <div className="text-2xl font-mono font-bold text-cyan-600">
                    {campaign.connectRate !== null ? `${campaign.connectRate}%` : '—'}
                  </div>
                </div>
              </div>

              <div className="mt-3">
                <TelephonyProvidersSection
                  providers={campaign.telephonyProviders}
                  total={campaign.telephonyTotal}
                />
              </div>
              
              <div className="mt-3 bg-white border border-gray-200 rounded-xl p-4 flex flex-col md:flex-row items-center gap-4">
                 <div className="text-[10px] uppercase tracking-widest text-gray-500 shrink-0">Contact Quality</div>
                 <div className="flex-1 w-full flex items-center gap-3 text-xs font-mono">
                    <span className="text-emerald-400">{campaign.contacts.accepted.toLocaleString()} Accepted</span>
                    <div className="flex-1 h-1.5 rounded-full overflow-hidden flex bg-rose-500/20">
                       <div className="h-full bg-emerald-500" style={{ width: `${(campaign.contacts.accepted / (campaign.contacts.total || 1)) * 100}%` }}></div>
                    </div>
                    <span className="text-rose-400">{campaign.contacts.rejected.toLocaleString()} Rejected</span>
                 </div>
              </div>
              
              {/* Schedule-Level Controls */}
              <div className="mt-4 bg-gray-50/50 border border-gray-100 rounded-xl p-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                <div>
                  <div className="text-xs font-bold text-gray-900 flex items-center gap-1.5">
                    <Settings2 className="w-3.5 h-3.5 text-gray-500" />
                    Schedule Dialer Controls
                  </div>
                  <div className="text-[10px] text-gray-500 mt-0.5">Overrides for this specific campaign schedule.</div>
                </div>
                
                <div className="flex items-center gap-4 flex-wrap">
                  {/* Effective isPaused = schedule-level OR global override */}
                  {(() => {
                    const gAll       = !!globalPauseSettings.pause_all_calls;
                    const gPrimary   = !!globalPauseSettings.pause_primary_calls;
                    const gCallbacks = !!globalPauseSettings.pause_scheduled_callbacks;

                    const effAll       = gAll || !!campaign.pauseSettings?.pause_all_calls;
                    const effPrimary   = gAll || gPrimary || !!campaign.pauseSettings?.pause_primary_calls;
                    const effCallbacks = gAll || gCallbacks || !!campaign.pauseSettings?.pause_scheduled_callbacks;

                    return (
                      <>
                        <div className="flex flex-col items-center gap-0.5">
                          <PauseButton
                            label="All"
                            isPaused={effAll}
                            onClick={(e) => { e.stopPropagation(); onToggleSchedulePause(campaign.id, 'pause_all_calls', !campaign.pauseSettings?.pause_all_calls, 'all calls for this schedule'); }}
                            disabled={gAll || updatingScheduleKey === `${campaign.id}-pause_all_calls`}
                            pauseColorClass="bg-rose-500 hover:bg-rose-600"
                          />
                          {gAll && <span className="text-[9px] text-rose-400 font-semibold">Global</span>}
                        </div>
                        <div className="w-px h-6 bg-gray-200 hidden sm:block"></div>
                        <div className="flex flex-col items-center gap-0.5">
                          <PauseButton
                            label="Primary"
                            isPaused={effPrimary}
                            onClick={(e) => { e.stopPropagation(); onToggleSchedulePause(campaign.id, 'pause_primary_calls', !campaign.pauseSettings?.pause_primary_calls, 'primary calls for this schedule'); }}
                            disabled={gAll || gPrimary || updatingScheduleKey === `${campaign.id}-pause_primary_calls`}
                            pauseColorClass="bg-amber-500 hover:bg-amber-600"
                          />
                          {(gAll || gPrimary) && <span className="text-[9px] text-amber-400 font-semibold">Global</span>}
                        </div>
                        <div className="w-px h-6 bg-gray-200 hidden sm:block"></div>
                        <div className="flex flex-col items-center gap-0.5">
                          <PauseButton
                            label="Callbacks"
                            isPaused={effCallbacks}
                            onClick={(e) => { e.stopPropagation(); onToggleSchedulePause(campaign.id, 'pause_scheduled_callbacks', !campaign.pauseSettings?.pause_scheduled_callbacks, 'callbacks for this schedule'); }}
                            disabled={gAll || gCallbacks || updatingScheduleKey === `${campaign.id}-pause_scheduled_callbacks`}
                            pauseColorClass="bg-violet-500 hover:bg-violet-600"
                          />
                          {(gAll || gCallbacks) && <span className="text-[9px] text-violet-400 font-semibold">Global</span>}
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </GlassCard>
  );
}

export default function CurrentStatsDashboard() {
  const { token, user } = useAuth();
  const [numDays, setNumDays] = useState(1);
  const [useDateRange, setUseDateRange] = useState(false);
  const [range, setRange] = useState(() => lookbackRange(1));
  const [agentId, setAgentId] = useState('');
  const [agentOptions, setAgentOptions] = useState([]);
  const [searchQ, setSearchQ] = useState('');
  const [activeFilter, setActiveFilter] = useState('all'); // all, live, needs_attention, clean
  const [refreshCountdown, setRefreshCountdown] = useState(60);
  const [updatingSetting, setUpdatingSetting] = useState(null);
  const [updatingScheduleKey, setUpdatingScheduleKey] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, title: '', message: '', onConfirm: null });

  const startDate = toYmd(range.start);
  const endDate = toYmd(range.end);
  const statsFilters = {
    numDays,
    startDate,
    endDate,
    useDateRange,
    agentId: agentId || null,
  };
  const { data, isLoading, isFetching, refetch, dataUpdatedAt } = useCurrentStats(statsFilters, token);
  const view = useDerivedViewModel(data, searchQ, activeFilter, statsFilters);

  // Keep agents seen in current-stats schedules so the dropdown still has
  // options after filtering down to a single agent_id.
  useEffect(() => {
    if (!data?.schedules) return;
    setAgentOptions((prev) => mergeAgentOptions(prev, agentsFromSchedules(data.schedules)));
  }, [data]);

  const selectLookback = (days) => {
    setNumDays(days);
    setUseDateRange(false);
    setRange(lookbackRange(days));
  };

  const selectCustomRange = (next) => {
    const clamped = clampDateRange(next.start, next.end);
    setRange({ ...clamped, preset: next.preset });
    setUseDateRange(true);
  };

  const togglePauseSetting = async (key, newValue) => {
    try {
      setUpdatingSetting(key);
      await axios.patch(`${apiUrl}/api/v1/admin/admin-settings`, 
        { [key]: newValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      refetch();
    } catch (error) {
      console.error("Failed to update setting", error);
      alert("Failed to update setting");
    } finally {
      setUpdatingSetting(null);
    }
  };

  const toggleSchedulePauseSetting = async (scheduleId, key, newValue) => {
    try {
      setUpdatingScheduleKey(`${scheduleId}-${key}`);
      await axios.patch(`${apiUrl}/api/v1/admin/schedules/${scheduleId}/pause-settings`, 
        { [key]: newValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      refetch();
    } catch (error) {
      console.error("Failed to update schedule setting", error);
      alert("Failed to update schedule setting");
    } finally {
      setUpdatingScheduleKey(null);
    }
  };

  const handleToggleGlobalPause = (key, currentValue, label) => {
    const newValue = !currentValue;
    const action = newValue ? 'pause' : 'resume';
    setConfirmDialog({
      isOpen: true,
      title: `Confirm Global ${action === 'pause' ? 'Pause' : 'Resume'}`,
      message: `Are you sure you want to ${action} ${label}? This will take immediate effect for all campaigns.`,
      onConfirm: async () => {
        await togglePauseSetting(key, newValue);
        setConfirmDialog({ isOpen: false });
      }
    });
  };

  const handleToggleSchedulePause = (scheduleId, key, newValue, label) => {
    const action = newValue ? 'pause' : 'resume';
    setConfirmDialog({
      isOpen: true,
      title: `Confirm Schedule ${action === 'pause' ? 'Pause' : 'Resume'}`,
      message: `Are you sure you want to ${action} ${label}?`,
      onConfirm: async () => {
        await toggleSchedulePauseSetting(scheduleId, key, newValue);
        setConfirmDialog({ isOpen: false });
      }
    });
  };

  useEffect(() => {
    setRefreshCountdown(60);
    const interval = setInterval(() => {
      setRefreshCountdown(c => Math.max(0, c - 1));
    }, 1000);
    return () => clearInterval(interval);
  }, [dataUpdatedAt]);

  if (isLoading && !view) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 flex flex-col gap-6 text-gray-900">
         <div className="h-16 bg-white rounded-2xl animate-pulse"></div>
         <div className="h-24 bg-white rounded-2xl animate-pulse"></div>
         <div className="grid grid-cols-1 md:grid-cols-2 gap-6"><div className="h-64 bg-white rounded-2xl animate-pulse"></div><div className="h-64 bg-white rounded-2xl animate-pulse"></div></div>
      </div>
    )
  }

  const lastUpdated = dataUpdatedAt ? formatTimeOnly(dataUpdatedAt, user?.timezone) : '';

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 selection:bg-cyan-100 font-sans relative overflow-x-hidden">
      {/* Background ambient glows */}
      <div className="fixed top-0 left-1/4 w-[800px] h-[600px] bg-cyan-100 rounded-full blur-[120px] pointer-events-none -translate-y-1/2"></div>
      <div className="fixed bottom-0 right-0 w-[600px] h-[600px] bg-violet-100 rounded-full blur-[100px] pointer-events-none translate-y-1/3"></div>

      <div className="relative z-10 p-4 md:p-8 space-y-6 max-w-[1600px] mx-auto">
        
        {/* A. Command Bar */}
        <header className="sticky top-4 z-50 rounded-2xl border border-gray-200 bg-gray-50/90 backdrop-blur-2xl shadow-sm px-5 py-3 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-xl font-bold text-gray-900 tracking-tight">Current Stats</h1>
            <p className="text-xs text-gray-500 uppercase tracking-widest mt-0.5">Executive Command Center</p>
          </div>
          
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex bg-white rounded-lg p-1 border border-gray-200">
              {NUM_DAYS_OPTIONS.map(d => (
                <button
                  key={d}
                  onClick={() => selectLookback(d)}
                  className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${!useDateRange && numDays === d ? 'bg-gray-200 text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-800 hover:bg-gray-50'}`}
                >
                  {d}d
                </button>
              ))}
            </div>

            <DateRangePicker
              value={range}
              onChange={selectCustomRange}
              align="right"
              triggerClassName={`h-8 px-2.5 text-xs font-medium rounded-lg border bg-white text-gray-800 hover:bg-gray-50 min-w-[10rem] ${useDateRange ? 'border-cyan-400 ring-1 ring-cyan-200' : 'border-gray-200'}`}
            />

            <AgentIdFilter
              value={agentId}
              onChange={setAgentId}
              agents={agentOptions}
            />
            
            <div className="relative flex-1 sm:flex-none w-full sm:w-auto">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input 
                type="text" 
                placeholder="Search campaigns..."
                value={searchQ}
                onChange={e => setSearchQ(e.target.value)}
                className="w-full sm:w-48 sm:focus:w-64 transition-all bg-white border border-gray-200 rounded-lg py-1.5 pl-9 pr-4 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-cyan-500"
              />
            </div>

            <button 
              onClick={() => refetch()}
              disabled={isFetching}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white border border-gray-200 text-sm hover:bg-gray-50 hover:bg-gray-100 transition-colors"
            >
              <RefreshCw className={`w-4 h-4 text-gray-500 ${isFetching ? 'animate-spin' : ''}`} />
              <span className="hidden md:inline text-gray-700">Refresh</span>
            </button>
            
            <div className="text-[10px] text-gray-400 font-mono text-right leading-tight hidden xl:block">
              <div>Updated {lastUpdated}</div>
              <div>Auto in {refreshCountdown}s</div>
            </div>
          </div>
        </header>

        {/* Global Dialer Controls */}
        {view?.adminSettings?.pause_settings && (
          <GlassCard className="p-5 flex flex-col xl:flex-row items-start xl:items-center justify-between gap-6 border-l-4 border-l-cyan-500">
            <div>
              <h2 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                <Settings2 className="w-4 h-4 text-gray-500" />
                Global Dialer Controls
              </h2>
              <p className="text-xs text-gray-500 mt-1">Master overrides for system-wide dialing behavior. These take immediate effect.</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-0 bg-gray-50/50 p-4 rounded-xl border border-gray-100 w-full xl:w-auto">
              <div className="flex items-center justify-between gap-4 md:pr-6">
                <div className="flex flex-col">
                  <span className="text-xs font-semibold text-gray-900">Pause All Calls</span>
                  <span className="text-[10px] text-gray-500">Halts all dialing</span>
                </div>
                <PauseButton 
                  isPaused={view.adminSettings.pause_settings.pause_all_calls} 
                  onClick={() => handleToggleGlobalPause('pause_all_calls', view.adminSettings.pause_settings.pause_all_calls, 'all global calls')} 
                  disabled={updatingSetting !== null}
                  pauseColorClass="bg-rose-500 hover:bg-rose-600"
                />
              </div>

              <div className="flex items-center justify-between gap-4 md:px-6 md:border-l border-gray-200 border-t md:border-t-0 pt-4 md:pt-0">
                <div className="flex flex-col">
                  <span className="text-xs font-semibold text-gray-900">Pause Primary</span>
                  <span className="text-[10px] text-gray-500">Only callbacks run</span>
                </div>
                <PauseButton 
                  isPaused={view.adminSettings.pause_settings.pause_primary_calls} 
                  onClick={() => handleToggleGlobalPause('pause_primary_calls', view.adminSettings.pause_settings.pause_primary_calls, 'global primary calls')} 
                  disabled={updatingSetting !== null}
                  pauseColorClass="bg-amber-500 hover:bg-amber-600"
                />
              </div>

              <div className="flex items-center justify-between gap-4 md:pl-6 md:border-l border-gray-200 border-t md:border-t-0 pt-4 md:pt-0">
                <div className="flex flex-col">
                  <span className="text-xs font-semibold text-gray-900">Pause Callbacks</span>
                  <span className="text-[10px] text-gray-500">Only new leads run</span>
                </div>
                <PauseButton 
                  isPaused={view.adminSettings.pause_settings.pause_scheduled_callbacks} 
                  onClick={() => handleToggleGlobalPause('pause_scheduled_callbacks', view.adminSettings.pause_settings.pause_scheduled_callbacks, 'global callbacks')} 
                  disabled={updatingSetting !== null}
                  pauseColorClass="bg-violet-500 hover:bg-violet-600"
                />
              </div>
            </div>
          </GlassCard>
        )}

        {/* B. Live Status Banner */}
        {view?.status && (
          <div className={`rounded-2xl border px-6 py-4 flex items-center gap-4 shadow-lg ${
            view.status.global === 'paused'  ? 'bg-rose-500/10 border-rose-500/20' :
            view.status.global === 'live'    ? 'bg-emerald-500/10 border-emerald-500/20' : 
            view.status.global === 'queued'  ? 'bg-amber-500/10 border-amber-500/20' : 
            'bg-white border-gray-200'
          }`}>
            <div className={`w-3 h-3 rounded-full shrink-0 ${
              view.status.global === 'paused'  ? 'bg-rose-400 animate-pulse shadow-[0_0_10px_rgba(251,113,133,0.8)]' :
              view.status.global === 'live'    ? 'bg-emerald-400 animate-pulse shadow-[0_0_10px_rgba(52,211,153,0.8)]' : 
              view.status.global === 'queued'  ? 'bg-amber-400 animate-pulse' : 
              'bg-gray-300'
            }`} />
            <div>
              <div className={`font-bold text-lg ${
                view.status.global === 'paused'  ? 'text-rose-500' :
                view.status.global === 'live'    ? 'text-emerald-400' : 
                view.status.global === 'queued'  ? 'text-amber-400' : 
                'text-gray-600'
              }`}>{view.status.message}</div>
              <div className="text-sm text-gray-600 mt-0.5">{view.status.description}</div>
            </div>
          </div>
        )}

        {/* C & D. Hero Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Live Pulse Hero */}
          <GlassCard className="p-8 relative overflow-hidden group">
            <div className="absolute top-0 right-0 w-64 h-64 bg-cyan-500/10 rounded-full blur-[80px] -translate-y-1/2 translate-x-1/4 group-hover:bg-cyan-500/20 transition-colors duration-700"></div>
            <div className="text-xs uppercase tracking-widest text-cyan-400/80 font-semibold mb-6">Live Conversations</div>
            
            <div className="flex items-center gap-8 mb-6 relative z-10">
              <div className={`text-8xl font-bold font-variant-numeric: tabular-nums tracking-tighter ${view?.global.live.activeCalls > 0 ? 'text-gray-900' : 'text-gray-300'}`}>
                {view?.global.live.activeCalls.toLocaleString()}
              </div>
              <WaveformIndicator active={view?.global.live.activeCalls > 0} />
            </div>
            
            <p className="text-gray-500 text-sm">AI agents currently speaking with humans in real-time.</p>
          </GlassCard>

          {/* Backlog Pressure Panel */}
          <GlassCard className="p-8">
            <div className="text-xs uppercase tracking-widest text-gray-500 font-semibold mb-6 flex items-center justify-between">
              <span>Backlog Pressure</span>
              <span className={`px-2 py-0.5 rounded text-[10px] ${view?.global.backlog.total > 0 ? 'bg-amber-500/20 text-amber-400' : 'bg-gray-100 text-gray-400'}`}>
                {view?.global.backlog.total > 0 ? 'Queues Active' : 'Clear'}
              </span>
            </div>
            
            <div className="text-5xl font-bold text-gray-900 mb-8 font-variant-numeric: tabular-nums">
              {view?.global.backlog.total.toLocaleString()} <span className="text-xl text-gray-400 font-normal">tasks</span>
            </div>

            <div className="space-y-4">
               {/* Pressure Bar */}
               <div className="h-3 w-full bg-gray-100 rounded-full overflow-hidden flex">
                  {view?.global.backlog.total > 0 ? (
                    <>
                      <div className="h-full bg-cyan-500" style={{ width: `${(view.global.backlog.primary / view.global.backlog.total) * 100}%` }} title="Primary"></div>
                      <div className="h-full bg-amber-500" style={{ width: `${(view.global.backlog.callbacks / view.global.backlog.total) * 100}%` }} title="Callbacks"></div>
                      <div className="h-full bg-violet-500" style={{ width: `${(view.global.backlog.pca / view.global.backlog.total) * 100}%` }} title="PCA"></div>
                    </>
                  ) : <div className="h-full w-full bg-gray-100"></div>}
               </div>

               <div className="flex justify-between text-xs font-mono">
                  <div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-cyan-500"/> <span className="text-gray-800">{view?.global.backlog.primary.toLocaleString()} Primary</span></div>
                  <div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-amber-500"/> <span className="text-gray-800">{view?.global.backlog.callbacks.toLocaleString()} Pending Callbacks</span></div>
                  <div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-violet-500"/> <span className="text-gray-800">{view?.global.backlog.pca.toLocaleString()} PCA</span></div>
               </div>
            </div>
          </GlassCard>
        </div>

        {/* E. KPI Bento Grid */}
        {/* Row 1: Total Dialled + dynamic status breakdown */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          <GlassCard className="p-5 flex flex-col justify-between h-32">
             <div className="text-[10px] uppercase tracking-widest text-gray-500">Total Dialled</div>
             <div className="text-3xl font-bold font-mono text-gray-900">{view?.global.throughput.totalCalls.toLocaleString()}</div>
          </GlassCard>
          {/* Dynamic status cards — one per status key returned by backend */}
          {view && Object.entries(view.global.throughput.dialedByStatus)
            .sort(([a], [b]) => a === 'completed' ? -1 : b === 'completed' ? 1 : a === 'failed' ? -1 : b === 'failed' ? 1 : a.localeCompare(b))
            .map(([status, count]) => {
              const isCompleted = status === 'completed';
              const isFailed = status === 'failed';
              const colorClass = isCompleted
                ? 'border-l-emerald-400 text-emerald-500'
                : isFailed
                ? 'border-l-rose-400 text-rose-500'
                : 'border-l-gray-300 text-gray-700';
              return (
                <GlassCard key={status} className={`p-5 flex flex-col justify-between h-32 border-l-2 ${colorClass.split(' ')[0]}`}>
                  <div className="text-[10px] uppercase tracking-widest text-gray-500">{status.replace(/-/g, ' ')}</div>
                  <div className={`text-3xl font-bold font-mono ${colorClass.split(' ')[1]}`}>{count.toLocaleString()}</div>
                </GlassCard>
              );
            })
          }
          <GlassCard className="p-5 flex flex-col justify-between h-32 border-l-2 border-l-cyan-400">
             <div className="text-[10px] uppercase tracking-widest text-gray-500">Connect Rate</div>
             <div className="text-3xl font-bold font-mono text-cyan-500">
               {view?.global.throughput.connectRate !== null ? `${view.global.throughput.connectRate}%` : '—'}
             </div>
          </GlassCard>
          <GlassCard className="p-5 flex flex-col justify-between h-32 border-l-2 border-l-amber-400">
             <div className="text-[10px] uppercase tracking-widest text-gray-500">Completed Callbacks</div>
             <div className="text-3xl font-bold font-mono text-amber-500">{view?.global.throughput.completedCallbacks.toLocaleString()}</div>
          </GlassCard>
          <GlassCard className="p-5 flex flex-col justify-between h-32 border-l-2 border-l-violet-400">
             <div className="text-[10px] uppercase tracking-widest text-gray-500">Calls &gt;15s</div>
             <div>
               <div className="text-3xl font-bold font-mono text-violet-500">{view?.global.throughput.completedOver15s.toLocaleString()}</div>
               <div className="text-[10px] font-mono text-gray-400 mt-1">
                 {view?.global.throughput.completed > 0
                   ? `${Math.round((view.global.throughput.completedOver15s / view.global.throughput.completed) * 100)}% of completed`
                   : 'of completed'}
               </div>
             </div>
          </GlassCard>
        </div>
        {/* Contacts row */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <GlassCard className="p-5 flex flex-col justify-between h-24">
             <div className="text-[10px] uppercase tracking-widest text-gray-500">Active Campaigns</div>
             <div className="text-3xl font-bold font-mono text-gray-900">{view?.global.campaigns.total}</div>
          </GlassCard>
          <GlassCard className="p-5 flex flex-col justify-between h-24">
             <div className="text-[10px] uppercase tracking-widest text-gray-500">Contacts Uploaded</div>
             <div>
               <div className="text-2xl font-bold font-mono text-gray-900 mb-1">{view?.global.contacts.total.toLocaleString()}</div>
               <div className="text-[10px] font-mono text-gray-400 flex gap-2">
                 <span className="text-cyan-400/80">{view?.global.contacts.accepted.toLocaleString()} acc</span>
                 <span className="text-gray-500">{view?.global.contacts.rejected.toLocaleString()} rej</span>
               </div>
             </div>
          </GlassCard>
          <GlassCard className="p-5 flex flex-col justify-between h-24">
             <div className="text-[10px] uppercase tracking-widest text-gray-500">Contact Acceptance Rate</div>
             <div className="text-2xl font-bold font-mono text-gray-900">
               {view?.global.contacts.total > 0 ? Math.round((view.global.contacts.accepted / view.global.contacts.total) * 100) : 0}%
             </div>
          </GlassCard>
        </div>

        <div>
          <div className="text-base font-semibold text-gray-900 mb-3">Unique contacts</div>
          <UniquePhoneSummary phones={view?.global.uniquePhones} />
        </div>

        <TelephonyProvidersSection
          providers={view?.global.throughput.telephonyProviders}
          total={view?.global.throughput.telephonyTotal}
        />

        {/* F. Campaign Fleet Section */}
        <div className="mt-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-4">
             <div>
               <h2 className="text-lg font-bold text-gray-900 mb-1">Active Campaign Fleet</h2>
               <p className="text-xs text-gray-500">Sorted by dynamic urgency score and live throughput.</p>
             </div>
             
             {/* Filter Chips */}
             <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
                {[
                  { id: 'all', label: 'All' },
                  { id: 'live', label: 'Live' },
                  { id: 'needs_attention', label: 'Needs Attention' },
                  { id: 'clean', label: 'Clean' }
                ].map(f => (
                  <button
                    key={f.id}
                    onClick={() => setActiveFilter(f.id)}
                    className={`whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-medium transition-colors border ${
                      activeFilter === f.id ? 'bg-cyan-500/20 border-cyan-500/30 text-cyan-300' : 'bg-gray-50 border-gray-200 text-gray-500 hover:bg-gray-100'
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
             </div>
          </div>

          <div className="space-y-3">
             {view?.campaigns.length === 0 ? (
                <GlassCard className="p-12 text-center flex flex-col items-center justify-center">
                   <Database className="w-8 h-8 text-gray-200 mb-4" />
                   <h3 className="text-lg font-bold text-gray-600 mb-2">No campaigns found</h3>
                   <p className="text-sm text-gray-400">Try widening your lookback filter or clearing your search.</p>
                </GlassCard>
             ) : (
                 view?.campaigns.map(c => (
                   <CampaignRow 
                     key={c.id} 
                     campaign={c} 
                     onToggleSchedulePause={handleToggleSchedulePause} 
                     updatingScheduleKey={updatingScheduleKey}
                     globalPauseSettings={view?.adminSettings?.pause_settings || {}}
                   />
                 ))
             )}
          </div>
        </div>

      </div>

      <ConfirmDialog 
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog({ isOpen: false })}
        isLoading={updatingSetting !== null || updatingScheduleKey !== null}
      />
    </div>
  );
}
